import {
  EuiCallOut,
  EuiEmptyPrompt,
  EuiIcon,
  EuiLink,
  EuiLoadingLogo,
  EuiMark,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from '@elastic/eui';
import type { MouseEvent, ReactNode } from 'react';
import { useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { getDomicileURL } from '../../model';
import { PageContext } from '../../page_container';

export enum SplashScreenMode {
  Default = 'Default',
  NoData = 'NoData',
  Loading = 'Loading',
}

export interface Params {
  mode: SplashScreenMode;
}

export function FundsSplashScreen({ mode }: Params) {
  const { domicileCode } = useContext(PageContext);
  const navigate = useNavigate();

  const [icon, title, body]: [ReactNode, string, string] = useMemo(() => {
    switch (mode) {
      case 'NoData':
        return [
          <EuiIcon key="search-no-data" type={'search'} size={'xl'} />,
          'There are no funds to display',
          'Try adjusting your filters or search query',
        ];
      case 'Loading':
        return [
          <EuiLoadingLogo key="search-loading" logo="search" size="xl" />,
          'Searching funds…',
          'Use filters and search bar to narrow down the search',
        ];
      default:
        return [
          <EuiIcon key="seatch-new" type={'search'} size={'xl'} />,
          "Explore funds and learn what's inside",
          'Use filters and search bar to narrow down the search',
        ];
    }
  }, [mode]);

  return (
    <EuiEmptyPrompt
      icon={icon}
      title={<h2>{title}</h2>}
      titleSize="s"
      style={{ maxWidth: '60em', display: 'flex' }}
      body={
        <div>
          <p>{body}</p>
          <EuiPanel color={'primary'} title={'Search query hints'}>
            <EuiCallOut title={'Search query hints'} iconType={'help'} color={'primary'} style={{ padding: 0 }}>
              <EuiPanel color={'warning'}>
                Go to the{' '}
                <EuiLink
                  href={getDomicileURL(domicileCode, '/assets')}
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    navigate(getDomicileURL(domicileCode, '/assets'));
                  }}
                >
                  Assets page
                </EuiLink>{' '}
                if you want to search for a single stock
              </EuiPanel>
              <EuiSpacer size="s" />
              <div style={{ textAlign: 'left' }}>
                <EuiText size="s">
                  <EuiMark>IE00BLPK3577</EuiMark> - displays fund with ISIN <strong>IE00BLPK3577</strong>
                </EuiText>
                <EuiSpacer size="m" />
                <EuiText size="s">
                  <EuiMark>iShares OR Cybersecurity</EuiMark> or <EuiMark>iShares Cybersecurity</EuiMark> - displays all{' '}
                  <strong>iShares</strong> funds or any funds with <strong>Cybersecurity</strong> in the name (
                  <strong>OR</strong> should be capitalized)
                </EuiText>
                <EuiSpacer size="m" />
                <EuiText size="s">
                  <EuiMark>iShares AND Cybersecurity</EuiMark> - displays all <strong>iShares</strong> funds with{' '}
                  <strong>Cybersecurity</strong> in the name (<strong>AND</strong> should be capitalized)
                </EuiText>
                <EuiSpacer size="m" />
                <EuiText size="s">
                  <EuiMark>Security Cybersecurity -iShares</EuiMark> - displays all funds with <strong>Security</strong>{' '}
                  or <strong>Cybersecurity</strong> in the name excluding those from <strong>iShares</strong>
                </EuiText>
              </div>
            </EuiCallOut>
          </EuiPanel>
        </div>
      }
    />
  );
}
